import { z } from 'zod';
import { IsoDateTime } from './common.js';
import { ConnectionMode } from './connections.js';
import { UserColor, type Role } from './users.js';

/**
 * Autorisation Google par personne (lot G) : « Mon compte Google », distincte de la connexion à l'appli.
 * Noms des scopes vérifiés dans la documentation Google (Gmail, Calendar, Chat, Sheets) le 12/10/2026.
 */
export const GOOGLE_SCOPES = {
  /** Gmail `users.messages.send` : envoyer les relances depuis sa boîte. */
  gmailSend: 'https://www.googleapis.com/auth/gmail.send',
  /** Gmail `users.drafts.create` : préparer des brouillons. */
  gmailCompose: 'https://www.googleapis.com/auth/gmail.compose',
  /** Gmail `users.messages.list` avec `q` (interdit avec gmail.metadata) et `messages.get`. */
  gmailReadonly: 'https://www.googleapis.com/auth/gmail.readonly',
  /**
   * Lot BB : Gmail `threads.modify` et `labels.create` : archiver et marquer lu un fil traité ou ignoré (libellé
   * `Cockpit/Ignoré`). Rien n'est jamais supprimé. Sans ce scope, l'archivage est sauté (« Reconnecte ton compte
   * Google pour l'archivage »), jamais la synchro.
   */
  gmailModify: 'https://www.googleapis.com/auth/gmail.modify',
  /** Calendar `events.list/insert/patch/delete` sur les agendas accessibles. */
  calendarEvents: 'https://www.googleapis.com/auth/calendar.events',
  /** Calendar `calendarList.list` : choisir l'agenda principal et tester la connexion (admin). */
  calendarList: 'https://www.googleapis.com/auth/calendar.calendarlist.readonly',
  /** Chat `spaces.messages.create` avec authentification utilisateur. */
  chatMessagesCreate: 'https://www.googleapis.com/auth/chat.messages.create',
  /** Chat `spaces.findDirectMessage` et `spaces.list`. */
  chatSpacesReadonly: 'https://www.googleapis.com/auth/chat.spaces.readonly',
  /** Chat `spaces.setup` : ouvrir la conversation privée si elle n'existe pas encore. */
  chatSpacesCreate: 'https://www.googleapis.com/auth/chat.spaces.create',
  /** Sheets `spreadsheets.get` et `values.batchGet` : imports des Sheets (admin). */
  sheetsReadonly: 'https://www.googleapis.com/auth/spreadsheets.readonly',
} as const;
export type GoogleScope = (typeof GOOGLE_SCOPES)[keyof typeof GOOGLE_SCOPES];

/** Libellés affichés dans « Mon compte Google ». */
export const GOOGLE_SCOPE_LABELS: Record<GoogleScope, string> = {
  [GOOGLE_SCOPES.gmailSend]: 'Gmail : envoyer des mails en ton nom',
  [GOOGLE_SCOPES.gmailCompose]: 'Gmail : préparer des brouillons',
  [GOOGLE_SCOPES.gmailReadonly]: 'Gmail : lire les échanges avec les clients',
  [GOOGLE_SCOPES.gmailModify]: 'Gmail : archiver et marquer lus les mails traités',
  [GOOGLE_SCOPES.calendarEvents]: 'Agenda : lire et modifier les rendez-vous',
  [GOOGLE_SCOPES.calendarList]: 'Agenda : lister tes agendas',
  [GOOGLE_SCOPES.chatMessagesCreate]: 'Google Chat : envoyer des messages',
  [GOOGLE_SCOPES.chatSpacesReadonly]: 'Google Chat : trouver les conversations et les espaces',
  [GOOGLE_SCOPES.chatSpacesCreate]: 'Google Chat : ouvrir une conversation privée',
  [GOOGLE_SCOPES.sheetsReadonly]: 'Sheets : lire les tableurs à importer',
};

/** Scopes demandés à tous les membres : Gmail, Agenda, Chat. */
export const MEMBER_GOOGLE_SCOPES: readonly GoogleScope[] = [
  GOOGLE_SCOPES.gmailSend,
  GOOGLE_SCOPES.gmailCompose,
  GOOGLE_SCOPES.gmailReadonly,
  // Lot BB : archivage des fils traités ou ignorés (support, fournisseurs).
  GOOGLE_SCOPES.gmailModify,
  GOOGLE_SCOPES.calendarEvents,
  GOOGLE_SCOPES.chatMessagesCreate,
  GOOGLE_SCOPES.chatSpacesReadonly,
  GOOGLE_SCOPES.chatSpacesCreate,
];

/** En plus pour les administrateurs : liste des agendas (Réglages, Tester) et Sheets (imports). */
export const ADMIN_GOOGLE_SCOPES: readonly GoogleScope[] = [
  ...MEMBER_GOOGLE_SCOPES,
  GOOGLE_SCOPES.calendarList,
  GOOGLE_SCOPES.sheetsReadonly,
];

export function requiredGoogleScopes(role: Role): readonly GoogleScope[] {
  return role === 'ADMIN' ? ADMIN_GOOGLE_SCOPES : MEMBER_GOOGLE_SCOPES;
}

export const GoogleScopeState = z.object({
  scope: z.string(),
  label: z.string(),
  granted: z.boolean(),
});
export type GoogleScopeState = z.infer<typeof GoogleScopeState>;

/** `GET /api/me/google` (et réponse de `DELETE /api/me/google`). */
export const GoogleAccountStatus = z.object({
  /** GOOGLE_CLIENT_ID et GOOGLE_CLIENT_SECRET sont renseignés sur le serveur. */
  oauthConfigured: z.boolean(),
  /** Mode appliqué à la connexion Google (en fake, les envois sont simulés). */
  mode: ConnectionMode,
  /** Un jeton de renouvellement valide est enregistré. */
  connected: z.boolean(),
  /** Compte Google autorisé (toujours l'adresse du membre). */
  email: z.string().nullable(),
  /** Scopes nécessaires à cette personne (selon son rôle), avec leur état. */
  scopes: z.array(GoogleScopeState),
  grantedScopes: z.array(z.string()),
  missingScopes: z.array(z.string()),
  connectedAt: IsoDateTime.nullable(),
  /** Déconnexion constatée (autorisation révoquée ou expirée côté Google). */
  disconnectedAt: IsoDateTime.nullable(),
  lastError: z.string().nullable(),
});
export type GoogleAccountStatus = z.infer<typeof GoogleAccountStatus>;

/** `GET /api/admin/google/calendars` : agendas de l'administrateur où il peut écrire. */
export const GoogleCalendar = z.object({
  id: z.string(),
  summary: z.string(),
  primary: z.boolean(),
  accessRole: z.string(),
});
export type GoogleCalendar = z.infer<typeof GoogleCalendar>;
export const GoogleCalendarList = z.array(GoogleCalendar);

/**
 * `GET /api/admin/google/calendar-members` (lot H) : agenda personnel de chaque membre actif. Il n'y a pas
 * d'agenda partagé : le cockpit lit l'agenda principal des membres qui ont connecté « Mon compte Google ».
 * `calendar` : `connected` (lu à chaque synchro), `not_connected` (pas de compte Google relié),
 * `scope_missing` (compte relié sans l'autorisation Agenda : le reconnecter).
 */
export const GoogleCalendarMember = z.object({
  userId: z.string(),
  name: z.string(),
  initials: z.string(),
  color: UserColor,
  calendar: z.enum(['connected', 'not_connected', 'scope_missing']),
  /** La personne du rôle « rendez-vous » du planning : R3, R4 et R5 sont posés dans son agenda. */
  meetingsRole: z.boolean(),
});
export type GoogleCalendarMember = z.infer<typeof GoogleCalendarMember>;
export const GoogleCalendarMemberList = z.array(GoogleCalendarMember);

/** `GET /api/admin/google/chat-spaces` : espaces Google Chat de l'administrateur. */
export const GoogleChatSpace = z.object({
  /** `spaces/AAAA…` */
  name: z.string(),
  displayName: z.string(),
  spaceType: z.string(),
});
export type GoogleChatSpace = z.infer<typeof GoogleChatSpace>;
export const GoogleChatSpaceList = z.array(GoogleChatSpace);

// ------------------------------------------------------------------ Boîte partagée (lot L, 13/10/2026)

/**
 * Boîte partagée (`contact@evolusite.fr`) : un compte Workspace sans mot de passe, ouvert à chacun par la
 * délégation Gmail. L'API Gmail ne passe pas par la délégation avec le jeton d'un membre : le cockpit utilise
 * un **compte de service avec délégation au niveau du domaine** (clé JSON chiffrée dans les secrets de la
 * connexion `google`, jamais renvoyée) et n'emprunte QUE les adresses listées ici, du domaine Workspace et
 * qui ne sont pas celles d'un membre.
 */
export const SHARED_MAILBOX_SCOPES = [
  GOOGLE_SCOPES.gmailReadonly,
  GOOGLE_SCOPES.gmailSend,
  GOOGLE_SCOPES.gmailCompose,
  // Lot BB : archivage des fils traités ou ignorés. À autoriser en plus dans la délégation au niveau du domaine.
  GOOGLE_SCOPES.gmailModify,
] as const;

/**
 * Lot BB : scopes du jeton de LECTURE et d'ENVOI d'une boîte partagée (ceux d'avant le lot BB). Le jeton
 * d'archivage (`gmail.modify`) est demandé à part : tant que l'administrateur ne l'a pas ajouté à la délégation,
 * seul l'archivage est sauté, la lecture et l'envoi continuent.
 */
export const SHARED_MAILBOX_BASE_SCOPES = [
  GOOGLE_SCOPES.gmailReadonly,
  GOOGLE_SCOPES.gmailSend,
  GOOGLE_SCOPES.gmailCompose,
] as const;

/** Adresse par défaut proposée à la première configuration. */
export const DEFAULT_SHARED_MAILBOX = 'contact@evolusite.fr';

/**
 * Valeur du choix « Envoyer depuis » qui désigne la boîte de la personne qui valide (les autres valeurs sont
 * les adresses des boîtes partagées prêtes).
 */
export const MAIL_FROM_ME = 'me';

/** Nombre maximal de boîtes partagées. */
export const MAX_SHARED_MAILBOXES = 5;

/**
 * État d'une boîte partagée : `untested` (jamais testée avec la clé actuelle), `ok` (profil Gmail lu en
 * l'empruntant), `error` (Google a refusé : le message dit quoi corriger), `refused` (adresse que le cockpit
 * refuse d'emprunter : hors domaine ou adresse d'un membre).
 */
export const SharedMailboxStatus = z.enum(['untested', 'ok', 'error', 'refused']);
export type SharedMailboxStatus = z.infer<typeof SharedMailboxStatus>;

export const SharedMailboxState = z.object({
  address: z.string(),
  status: SharedMailboxStatus,
  testedAt: IsoDateTime.nullable(),
  message: z.string().nullable(),
});
export type SharedMailboxState = z.infer<typeof SharedMailboxState>;

/** `GET /api/admin/google/shared-mailboxes` : jamais la clé, seulement l'identité du compte de service. */
export const SharedMailboxSettings = z.object({
  /** Démonstration (FORCE_FAKE) : boîte partagée simulée, aucune clé nécessaire. */
  demo: z.boolean(),
  /** Compte de service de la clé enregistrée (null : aucune clé). */
  serviceAccount: z
    .object({
      clientEmail: z.string(),
      /** « ID client » à autoriser dans la console d'administration (délégation au niveau du domaine). */
      clientId: z.string(),
      projectId: z.string().nullable(),
    })
    .nullable(),
  mailboxes: z.array(SharedMailboxState),
  /** Boîtes prêtes (testées) : proposées dans « Envoyer depuis » et lues par la synchro Gmail. */
  ready: z.array(z.string()),
  /** Scopes à autoriser pour l'ID client (console d'administration Google). */
  scopes: z.array(z.string()),
  /** Domaine Workspace : seules ses adresses peuvent être des boîtes partagées. */
  domain: z.string(),
});
export type SharedMailboxSettings = z.infer<typeof SharedMailboxSettings>;

/**
 * `PUT /api/admin/google/shared-mailboxes`. `serviceAccountKey` : le contenu du fichier JSON du compte de
 * service (le remplace ; `null` l'efface ; absent : inchangé). `addresses` : la liste complète des boîtes.
 */
export const UpdateSharedMailboxBody = z
  .object({
    serviceAccountKey: z.string().trim().min(1).max(20_000).nullable().optional(),
    addresses: z
      .array(
        z
          .string()
          .trim()
          .toLowerCase()
          .pipe(z.email({ message: 'Adresse e-mail invalide' })),
      )
      .max(MAX_SHARED_MAILBOXES)
      .optional(),
  })
  .refine((body) => body.serviceAccountKey !== undefined || body.addresses !== undefined, {
    message: 'Aucune modification demandée',
  });
export type UpdateSharedMailboxBody = z.input<typeof UpdateSharedMailboxBody>;

/** `POST /api/admin/google/shared-mailboxes/test` : chaque adresse empruntée, son profil Gmail lu. */
export const SharedMailboxTestResult = z.object({
  ok: z.boolean(),
  message: z.string(),
  settings: SharedMailboxSettings,
});
export type SharedMailboxTestResult = z.infer<typeof SharedMailboxTestResult>;

// ------------------------------------------------------------------ Lot S : historique des e-mails

/** Profondeur de la reprise de l'historique Gmail, en mois (24 par défaut, 60 au plus). */
export const DEFAULT_GMAIL_HISTORY_MONTHS = 24;
export const MAX_GMAIL_HISTORY_MONTHS = 60;

/** `settings.gmailHistory` : jusqu'où remonter dans chaque boîte (membres et boîtes partagées). */
export const GmailHistorySettings = z.object({
  months: z.int().min(1).max(MAX_GMAIL_HISTORY_MONTHS),
});
export type GmailHistorySettings = z.infer<typeof GmailHistorySettings>;

/**
 * Avancement de la reprise pour une boîte : `pending` (pas encore commencée), `running` (en cours, reprend où
 * elle s'est arrêtée), `done` (profondeur atteinte), `error` (dernière passe en échec, reprise à la suivante).
 */
export const GmailHistoryState = z.enum(['pending', 'running', 'done', 'error']);
export type GmailHistoryState = z.infer<typeof GmailHistoryState>;

export const GmailHistoryMailbox = z.object({
  /** `user:<id>` ou `shared:<adresse>`. */
  key: z.string(),
  kind: z.enum(['member', 'shared']),
  /** Prénom du membre ou adresse de la boîte partagée. */
  label: z.string(),
  state: GmailHistoryState,
  /** Mois déjà repris (de maintenant vers le passé), au plus `monthsTarget`. */
  monthsDone: z.int().min(0),
  monthsTarget: z.int().min(1),
  /** Jusqu'où les échanges sont repris (date AAAA-MM-JJ), null avant la première passe. */
  coveredFrom: z.string().nullable(),
  messages: z.int().min(0),
  lastError: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type GmailHistoryMailbox = z.infer<typeof GmailHistoryMailbox>;

/** `GET/PUT /api/admin/google/gmail-history` : profondeur et avancement par boîte. */
export const GmailHistoryStatus = z.object({
  months: z.int().min(1).max(MAX_GMAIL_HISTORY_MONTHS),
  mailboxes: z.array(GmailHistoryMailbox),
});
export type GmailHistoryStatus = z.infer<typeof GmailHistoryStatus>;

/**
 * « Charger tout l'historique de ce client » : `POST /api/clients/:id/messages/history` (202, tâche en file)
 * puis `GET` du même chemin pour l'avancement. Sans limite de date, seulement les adresses de ce client.
 */
export const ClientHistoryStatus = z.object({
  clientId: z.uuid(),
  state: z.enum(['idle', 'queued', 'running', 'done', 'error']),
  requestedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  /** Messages lus dans les boîtes pendant la dernière reprise (doublons compris). */
  messages: z.int().min(0),
  lastError: z.string().nullable(),
});
export type ClientHistoryStatus = z.infer<typeof ClientHistoryStatus>;
